import _ from 'lodash';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FakeLink } from '@/core/FakeLink';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { SeeqLogo2026 } from '@/core/SeeqLogoOrange.atom';
import { ForgotPasswordConfirmation } from '@/main/auth/ForgotPasswordConfirmation.atom';
import { ForgotPasswordForm } from '@/main/auth/ForgotPasswordForm.organism';
import { LoginForm } from '@/main/auth/LoginForm.organism';
import { LoginInfoMessage } from '@/main/auth/LoginInfoMessage.molecule';
import { RegisterForm } from '@/main/auth/RegisterForm.organism';
import { returnToPreviousState } from '@/main/routing.utilities';
import {
  authDefaultProviderId,
  getLoginMessage,
  atUserLimit as isAtUserLimit,
  registrationEnabled as isRegistrationEnabled,
  isSelfServicePasswordResetEnabled,
  seeqFullReleaseIdentifier,
} from '@/services/systemConfiguration.utilities';
import { isCsrfSet } from '@/utilities/auth.utilities';
import { fetchAuthenticationProviders } from '@/utilities/authentication.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { switchModeAndTheme } from '@/utilities/utilities';
import { SeeqNames } from '@seeqdev/constants';
import { getStorageSafely } from '@seeqdev/utils';
import type { PostAuthError } from '@/main/auth/hooks/useLogin.hook';
import type { DatasourceOutputV1 } from '@seeqdev/api/model/DatasourceOutputV1';

enum DisplayView {
  Login,
  Register,
  ForgotPassword,
  ForgotPasswordConfirmation,
}

export const Login = () => {
  const { t } = useTranslation();

  const [availableDomains, setAvailableDomains] = useState<DatasourceOutputV1[]>([]);
  const [displayView, setDisplayView] = useState(DisplayView.Login);
  const [config, setConfig] = useState<any>({});
  const authenticated = isCsrfSet();

  const { atUserLimit, registrationEnabled, selfServicePasswordResetEnabled } = config;

  const [searchParams] = useSearchParams();
  const requestedDirectoryId = searchParams.get('directoryId');

  const findDomain = (datasourceId: string | null) => _.find(availableDomains, ['datasourceId', datasourceId]);

  const seeqDomain = findDomain(SeeqNames.LocalDatasources.Authentication.DatasourceId);
  const requestedDomain = findDomain(requestedDirectoryId);

  const getDomain = () =>
    requestedDomain ||
    findDomain(getStorageSafely().getItem('sqDomain')) ||
    _.chain(availableDomains)
      .reject(['datasourceId', SeeqNames.LocalDatasources.Authentication.DatasourceId])
      .sortBy((domain) => {
        const defaultId = authDefaultProviderId();
        if (!defaultId) {
          return 1;
        }
        // Sort the list of domains so that the defaultId is first
        return domain.datasourceId === defaultId ? 0 : 1;
      })
      .first()
      .value() ||
    seeqDomain ||
    ({} as DatasourceOutputV1);

  const [domain, setDomain] = useState<DatasourceOutputV1>(() => getDomain());

  useEffect(() => {
    // check if we're already logged in and if so return to returnTo or workbooks
    if (authenticated) {
      returnToPreviousState();
    }

    switchModeAndTheme(false, 'topic');

    setConfig({
      atUserLimit: isAtUserLimit(),
      registrationEnabled: isRegistrationEnabled(),
      loginMessage: getLoginMessage(),
      selfServicePasswordResetEnabled: isSelfServicePasswordResetEnabled(),
    });

    // Requests a list of supported authentication providers
    fetchAuthenticationProviders(requestedDirectoryId ?? undefined).then(setAvailableDomains);
  }, []);

  useEffect(() => {
    setDomain(getDomain());
  }, [seeqDomain, availableDomains]);

  const isSeeqAuthSelected = domain.datasourceId === SeeqNames.LocalDatasources.Authentication.DatasourceId;

  const displayLogin = () => displayView === DisplayView.Login;
  const displayRegister = () => displayView === DisplayView.Register;
  const displayForgotPassword = () => displayView === DisplayView.ForgotPassword;
  const displayForgotPasswordConfirmation = () => displayView === DisplayView.ForgotPasswordConfirmation;

  const setDisplayLogin = () => setDisplayView(DisplayView.Login);
  const setDisplayRegister = () => setDisplayView(DisplayView.Register);
  const setDisplayForgotPassword = () => setDisplayView(DisplayView.ForgotPassword);
  const setDisplayForgotPasswordConfirmation = () => setDisplayView(DisplayView.ForgotPasswordConfirmation);

  const updateDomain = (value: string, property = 'datasourceId') => {
    const newDomain = _.find(availableDomains, [property, value]) || domain;
    setDomain(newDomain);

    return newDomain;
  };

  // all post auth error types can be handled in this function
  const handlePostAuthError = (error: PostAuthError) => {
    if (error.failureType === 'PasswordChangeRequired') {
      errorToast({ messageKey: 'LOGIN_PANEL.POST_AUTH_ERRORS.PASSWORD_CHANGE_REQUIRED' });
      setDisplayForgotPassword();
    }
    if (error.failureType === 'PasswordExpired') {
      if (isSelfServicePasswordResetEnabled()) {
        errorToast({ messageKey: 'PASSWORD_WARNINGS.USER_PASSWORD_EXPIRED' });
        setDisplayForgotPassword();
      } else {
        errorToast({ messageKey: 'PASSWORD_WARNINGS.USER_PASSWORD_EXPIRED_CONTACT_ADMIN' });
      }
    }
    if (error.failureType === 'OrganizationApprovalRequired') {
      errorToast({ messageKey: 'LOGIN_PANEL.POST_AUTH_ERRORS.ORGANIZATION_APPROVAL_REQUIRED' });
    }
    if (error.failureType === 'OrganizationSetupIncomplete') {
      errorToast({ messageKey: 'LOGIN_PANEL.POST_AUTH_ERRORS.ORGANIZATION_SETUP_INCOMPLETE' });
    }
  };

  const renderForms = () => {
    if (_.isEmpty(availableDomains) || _.isEmpty(domain) || _.isEmpty(config)) {
      return (
        <div className="flexRowContainer tw:items-center">
          <IconWithSpinner spinning={true} />
        </div>
      );
    }

    return (
      <>
        {displayLogin() && (
          <LoginForm
            domain={domain}
            availableDomains={availableDomains}
            updateDomain={updateDomain}
            postAuthErrorFn={handlePostAuthError}
          />
        )}
        {displayRegister() &&
          (atUserLimit ? (
            <div>{t('LOGIN_PANEL.USER_LIMIT')}</div>
          ) : (
            <RegisterForm seeqDomain={seeqDomain} domain={domain} toggleDisplay={setDisplayLogin} />
          ))}
        {displayForgotPassword() && (
          <ForgotPasswordForm
            domain={domain}
            seeqDomain={seeqDomain}
            closeFn={setDisplayLogin}
            confirmationFn={setDisplayForgotPasswordConfirmation}
          />
        )}
        {displayForgotPasswordConfirmation() && <ForgotPasswordConfirmation closeFn={setDisplayLogin} />}

        <div className="tw:mt-5">
          {selfServicePasswordResetEnabled && displayLogin() && isSeeqAuthSelected && (
            <div className="text-center tw:mt-sq-5">
              <span>{t('LOGIN_PANEL.FORGOT_PASSWORD')}</span>
              &nbsp;
              <FakeLink onClick={setDisplayForgotPassword} testId="forgotPasswordLink" extraClassNames="tw:underline">
                {t('LOGIN_PANEL.RESET_PASSWORD')}
              </FakeLink>
            </div>
          )}
          {registrationEnabled && displayLogin() && isSeeqAuthSelected && (
            <div className="text-center tw:mt-sq-5">
              <span>{t('LOGIN_PANEL.NO_ACCOUNT')}</span>
              &nbsp;
              <FakeLink onClick={setDisplayRegister} testId="registerNowLink">
                {t('LOGIN_PANEL.REGISTER_NOW')}
              </FakeLink>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="loginFooter sq-darkish-gray font-size-smaller tw:pr-sq-10 tw:pl-sq-10">
        <small>{seeqFullReleaseIdentifier()}</small>
      </div>

      <div className="flexRowContainer fullViewport">
        <div
          data-testid="loginBanner"
          className="tw:flex tw:h-sq-48 tw:w-full tw:shrink-0 tw:items-center tw:justify-center tw:bg-sq-login-maroon">
          <span className="tw:text-sq-17 tw:font-semibold tw:text-sq-login-cream">{t('LOGIN_PANEL.BANNER')}</span>
        </div>

        <div
          data-testid="loginPage"
          className="flexRowContainer splashScreenLight loginBackdrop flexCenter tw:min-h-0 tw:grow"
          style={{ backgroundImage: 'url(/login-image/), var(--sq-login-gradient)' }}>
          <div className="loginPanel loginPanelRedesigned flexRowContainer min-width-300 overlayPanel tw:w-3/8">
            <div className="loginCardFrame width-maximum">
              <div className="loginCardInner">
                <div className="tw:p-sq-40">
                  <div data-testid="logoAndSlogan" className="flexRowContainer flexCenter mb25">
                    <SeeqLogo2026 />
                    <h4 className="loginSlogan mt8">{t('LOGIN_PANEL.SLOGAN_V2')}</h4>
                    {displayRegister() && !atUserLimit && (
                      <span className="text-color-default">{t('LOGIN_PANEL.REGISTRATION_PROMPT')}</span>
                    )}
                  </div>

                  {config.loginMessage !== '' ? <LoginInfoMessage content={config.loginMessage} /> : null}
                  {renderForms()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
